import React, { forwardRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Mesmo destino do CTA de Upgrade do Sidebar (página de preços da landing).
export const SALES_PRECOS_URL = 'https://darkplanner.com.br/precos';

const PLAN_LABELS = { free: 'Free', basic: 'Basic', lite: 'Lite' };

/**
 * PremiumUpsell — card de venda padrão dos recursos premium (Niche Finder,
 * Vídeos Virais, Insights de Canal...). Visual único da marca; cada recurso
 * passa a própria copy via props (strings JÁ traduzidas pelo chamador).
 *
 * Props:
 *   badge: string         — pílula gradiente no topo (ex.: "NICHE FINDER")
 *   headline: string      — H2 (ex.: "Desbloqueie o Niche Finder")
 *   subhead: string
 *   bullets: [{ Icon, text }]
 *   statItems?: [{ value, label }]  — linha de números (opcional)
 *   urgencyText?: string            — frase de urgência (opcional)
 *   ctaLabel: string
 *   planType?: string     — 'free' | 'basic' | 'lite' (pílula "Plano atual")
 *   pulseKey?: number     — >0 retriggera o pulso único do CTA (key remonta)
 *
 * As duas strings genéricas (Plano atual / Ver todos os planos) saem das
 * chaves compartilhadas viralNiches.upsell_current_plan / upsell_secondary.
 */
const PremiumUpsell = forwardRef(({
  badge,
  headline,
  subhead,
  bullets = [],
  statItems = [],
  urgencyText = null,
  ctaLabel,
  planType,
  pulseKey = 0,
}, ref) => {
  const { t } = useTranslation();
  const planLabel = PLAN_LABELS[(planType || 'free').toLowerCase()] || 'Free';

  return (
    <section
      ref={ref}
      className="dp-top-accent"
      style={{
        maxWidth: '720px',
        margin: '0 auto',
        background: 'var(--dp-bg-card)',
        border: '1px solid var(--dp-border-subtle)',
        borderRadius: '16px',
        padding: '40px 36px',
        textAlign: 'center',
        position: 'relative',
        fontFamily: 'var(--dp-font)',
      }}
    >
      {/* Badge da marca */}
      <span style={{
        display: 'inline-block',
        padding: '4px 14px',
        borderRadius: '9999px',
        background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
        color: '#fff',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '1px',
        marginBottom: '16px',
      }}>
        {badge}
      </span>

      {/* Headline */}
      <h2 style={{
        margin: '0 0 10px',
        fontSize: '27px',
        fontWeight: 800,
        letterSpacing: '-0.5px',
        color: 'var(--dp-text-primary)',
        lineHeight: 1.2,
      }}>
        {headline}
      </h2>

      {/* Subhead */}
      <p style={{
        margin: '0 auto 24px',
        maxWidth: '540px',
        fontSize: '14px',
        lineHeight: 1.6,
        color: 'var(--dp-text-secondary)',
      }}>
        {subhead}
      </p>

      {/* Stats row (opcional) — prova com números reais */}
      {statItems.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '36px',
          flexWrap: 'wrap',
          marginBottom: '26px',
        }}>
          {statItems.map((s, i) => (
            <div key={i}>
              <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--dp-blue-500, #0DAEFF)', lineHeight: 1.2 }}>
                {s.value}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', marginTop: '2px' }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bullets de valor */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '12px',
        textAlign: 'left',
        marginBottom: '24px',
      }}>
        {bullets.map(({ Icon, text }, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{
              width: '28px',
              height: '28px',
              borderRadius: '8px',
              background: 'rgba(13, 174, 255, 0.10)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '1px',
            }}>
              <Icon size={15} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
            </span>
            <span style={{ fontSize: '13px', lineHeight: 1.5, color: 'var(--dp-text-secondary)' }}>
              {text}
            </span>
          </div>
        ))}
      </div>

      {/* Urgência (opcional) */}
      {urgencyText && (
        <p style={{
          margin: '0 0 22px',
          fontSize: '12px',
          color: 'var(--dp-text-tertiary)',
          lineHeight: 1.5,
        }}>
          {urgencyText}
        </p>
      )}

      {/* CTA padrão da marca — key={pulseKey} remonta e roda o pulso 1x */}
      <a
        key={pulseKey}
        className={`dp-btn-cta${pulseKey > 0 ? ' dp-pulse-once' : ''}`}
        href={SALES_PRECOS_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none' }}
      >
        {ctaLabel}
        <ArrowRight size={16} />
      </a>

      {/* Plano atual + link secundário */}
      <div style={{
        marginTop: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '14px',
        flexWrap: 'wrap',
      }}>
        <span style={{
          padding: '4px 12px',
          borderRadius: '9999px',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid var(--dp-border-medium)',
          fontSize: '11px',
          color: 'var(--dp-text-secondary)',
        }}>
          {t('viralNiches.upsell_current_plan', { plan: planLabel })}
        </span>
        <a
          href={SALES_PRECOS_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: 'var(--dp-blue-500, #0DAEFF)',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
        >
          {t('viralNiches.upsell_secondary')}
        </a>
      </div>
    </section>
  );
});

PremiumUpsell.displayName = 'PremiumUpsell';

export default PremiumUpsell;
